import { AlertTriangle, RefreshCw, Home, ArrowLeft, Bug } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible';
import type { ErrorFallbackProps } from './ErrorBoundary';
import { useState } from 'react';

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  resetError,
  level = 'component',
  eventId,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleReset = async () => {
    setIsResetting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    resetError();
    setIsResetting(false);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const getErrorTitle = () => {
    switch (level) {
      case 'app':
        return 'Application Error';
      case 'route':
        return 'Page Error';
      case 'component':
        return 'Component Error';
      default:
        return 'Something went wrong';
    }
  };

  const getErrorDescription = () => {
    switch (level) {
      case 'app':
        return 'The application has encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.';
      case 'route':
        return 'This page failed to load properly. You can try refreshing the page or navigating to a different section.';
      case 'component':
        return 'A component on this page has encountered an error. You can try refreshing or continue using other parts of the application.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const renderActions = () => {
    const commonActions = [
      <Button
        key="retry"
        onClick={handleReset}
        disabled={isResetting}
        className="gap-2"
        variant="default"
      >
        {isResetting ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="h-4 w-4" />
        )}
        Try Again
      </Button>
    ];

    switch (level) {
      case 'app':
        return [
          ...commonActions,
          <Button
            key="reload"
            onClick={handleReload}
            variant="outline"
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reload Page
          </Button>
        ];
      case 'route':
        return [
          ...commonActions,
          <Button
            key="back"
            onClick={handleGoBack}
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>,
          <Button
            key="home"
            onClick={handleGoHome}
            variant="outline"
            className="gap-2"
          >
            <Home className="h-4 w-4" />
            Go Home
          </Button>
        ];
      case 'component':
      default:
        return [
          ...commonActions,
          <Button
            key="reload"
            onClick={handleReload}
            variant="outline"
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reload Page
          </Button>
        ];
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
          </div>
          <CardTitle className="text-2xl">{getErrorTitle()}</CardTitle>
          <CardDescription className="text-base">
            {getErrorDescription()}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {eventId && (
            <Alert>
              <Bug className="h-4 w-4" />
              <AlertDescription>
                <strong>Error ID:</strong> {eventId}
                <br />
                <span className="text-sm text-muted-foreground">
                  Please include this ID when contacting support.
                </span>
              </AlertDescription>
            </Alert>
          )}

          {import.meta.env.DEV && error && (
            <Collapsible>
              <CollapsibleTrigger
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                onClick={() => setShowDetails(!showDetails)}
              >
                <Bug className="h-4 w-4" />
                {showDetails ? 'Hide' : 'Show'} Technical Details
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="rounded-lg bg-muted p-4 text-sm font-mono space-y-2">
                  <div>
                    <strong>Error:</strong> {error.message}
                  </div>
                  {error.stack && (
                    <div>
                      <strong>Stack Trace:</strong>
                      <pre className="mt-1 whitespace-pre-wrap text-xs">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                  {errorInfo?.componentStack && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="mt-1 whitespace-pre-wrap text-xs">
                        {errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>

        <CardFooter className="flex flex-wrap gap-2 justify-center">
          {renderActions()}
        </CardFooter>
      </Card>
    </div>
  );
};

export const NetworkErrorFallback: React.FC<ErrorFallbackProps> = (props) => (
  <ErrorFallback
    {...props}
    level="component"
  />
);

export const NotFoundFallback: React.FC<{ onNavigateHome?: () => void }> = ({
  onNavigateHome = () => window.location.href = '/'
}) => (
  <div className="flex items-center justify-center min-h-[400px] p-4">
    <Card className="w-full max-w-lg text-center">
      <CardHeader>
        <div className="flex justify-center mb-4">
          <AlertTriangle className="h-12 w-12 text-muted-foreground" />
        </div>
        <CardTitle className="text-2xl">Page Not Found</CardTitle>
        <CardDescription>
          The page you're looking for doesn't exist or has been moved.
        </CardDescription>
      </CardHeader>
      <CardFooter className="flex justify-center gap-2">
        <Button onClick={() => window.history.back()} variant="outline" className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Go Back
        </Button>
        <Button onClick={onNavigateHome} className="gap-2">
          <Home className="h-4 w-4" />
          Go Home
        </Button>
      </CardFooter>
    </Card>
  </div>
);

export const LoadingErrorFallback: React.FC<ErrorFallbackProps & { onRetry?: () => void }> = ({
  onRetry,
  ...props
}) => (
  <div className="flex items-center justify-center min-h-[200px] p-4">
    <Card className="w-full max-w-md text-center">
      <CardHeader>
        <div className="flex justify-center mb-4">
          <AlertTriangle className="h-8 w-8 text-muted-foreground" />
        </div>
        <CardTitle className="text-lg">Failed to Load</CardTitle>
        <CardDescription>
          This content could not be loaded. Please try again.
        </CardDescription>
      </CardHeader>
      <CardFooter className="flex justify-center">
        <Button 
          onClick={onRetry ?? props.resetError} 
          variant="outline" 
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
      </CardFooter>
    </Card>
  </div>
);